import { styled } from '@uhg-abyss/web/tools/styled';
import { Alert } from '@uhg-abyss/web/ui/Alert';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { Link } from '@uhg-abyss/web/ui/Link';
import { V1Table } from '@uhg-abyss/web/ui/Table';

import {
  BANNER_DEEPLINKS_MESSAGE,
  BASE_URLS,
  COLUMN_ENVIRONMENT,
  COLUMN_PROPERTY,
  COLUMN_URL,
  COLUMN_VALUE,
  ENV_DEV,
  ENV_INT,
  ENV_PROD,
  ENV_STAGE,
  ENV_UAT,
  GUEST_SUFFIX,
  LABEL_COVERAGE_TYPE,
  LABEL_LANGUAGE,
  LABEL_LOB,
  LABEL_PLAN_NAME,
  LABEL_POLICY_NUMBER,
  LABEL_RECIPROCITY_ID,
  SAMPLE_COVERAGE_TYPE,
  SAMPLE_LANGUAGE,
  SAMPLE_LOB,
  SAMPLE_PLAN_NAME,
  SAMPLE_POLICY_NUMBER,
  SAMPLE_RECIPROCITY_ID,
  TITLE_DEEPLINKS,
  TITLE_DEEPLINK_PROPERTIES,
  TITLE_ENVIRONMENT_URLS,
  TITLE_EXAMPLE_URLS,
} from './constants';

const TableWrapper = styled('div', {
  fontFamily: '$primary',
  backgroundColor: '$bgHeaderColor',
  padding: '1em',
  marginTop: '16px',

  '.abyss-table-root': {
    backgroundColor: 'white',
    border: '1px solid $core.color.neutral.20',
    borderRadius: '4px',
    overflow: 'hidden',
  },

  '.abyss-table-head': {
    backgroundColor: '$core.color.brand.100',
  },

  '.abyss-table-header-row': {
    backgroundColor: '$core.color.brand.100',
  },

  '.abyss-table-column-header': {
    backgroundColor: '$core.color.brand.100',
    color: 'white',
    fontWeight: 'bold',
    padding: '12px',
    fontSize: '14px',
    textAlign: 'left',
    borderBottom: '2px solid $core.color.neutral.20',
  },

  '.abyss-table-body': {
    backgroundColor: 'white',
  },

  '.abyss-table-row': {
    '&:nth-of-type(even)': {
      backgroundColor: '#FAFAFA',
    },
    '&:hover': {
      backgroundColor: '$core.color.neutral.10',
    },
  },

  '.abyss-table-cell': {
    padding: '12px',
    borderBottom: '1px solid $core.color.neutral.20',
    fontSize: '13px',
    verticalAlign: 'top',
    lineHeight: '1.5',
    wordBreak: 'break-all',
  },
});

const PropertyName = styled('strong', {});

const UrlLink = styled(Link, {
  fontSize: '13px',
  wordBreak: 'break-all',
  display: 'block',
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

export const DeeplinksBanner = styled(Alert, {
  '& .abyss-alert-icon': {
    fontSize: '20px !important',
    margin: '8px',
  },
  '& .abyss-alert-container': {
    marginBottom: '24px',
    borderColor: '$core.color.brand.60',
    backgroundColor: '$core.color.brand.5',
  },
  '& .abyss-alert-children-container, & .abyss-alert-title': {
    fontSize: '14px',
    fontWeight: 400,
    textAlign: 'left',
    padding: '8px',
  },
  '& .abyss-alert-title': { fontWeight: 700, padding: '8px' },
});

export const SampleDeeplinkTable = () => {
  const propertiesColumns = [
    { key: 'property', name: COLUMN_PROPERTY },
    { key: 'value', name: COLUMN_VALUE },
  ];

  // Sample deeplink parameters
  const sampleParams = {
    planName: SAMPLE_PLAN_NAME,
    coverageType: SAMPLE_COVERAGE_TYPE,
    reciprocityId: SAMPLE_RECIPROCITY_ID,
    lob: SAMPLE_LOB,
    policyId: SAMPLE_POLICY_NUMBER,
    language: SAMPLE_LANGUAGE,
  };

  // Base64 encode the sample parameters using Buffer (consistent with codebase standards)
  const encodedParams = Buffer.from(JSON.stringify(sampleParams)).toString(
    'base64'
  );

  const propertiesRows = [
    {
      id: 1,
      property: <PropertyName>{LABEL_PLAN_NAME}</PropertyName>,
      value: SAMPLE_PLAN_NAME,
    },
    {
      id: 2,
      property: <PropertyName>{LABEL_COVERAGE_TYPE}</PropertyName>,
      value: SAMPLE_COVERAGE_TYPE,
    },
    {
      id: 3,
      property: <PropertyName>{LABEL_RECIPROCITY_ID}</PropertyName>,
      value: SAMPLE_RECIPROCITY_ID,
    },
    {
      id: 4,
      property: <PropertyName>{LABEL_LOB}</PropertyName>,
      value: SAMPLE_LOB,
    },
    {
      id: 5,
      property: <PropertyName>{LABEL_POLICY_NUMBER}</PropertyName>,
      value: SAMPLE_POLICY_NUMBER,
    },
    {
      id: 6,
      property: <PropertyName>{LABEL_LANGUAGE}</PropertyName>,
      value: SAMPLE_LANGUAGE,
    },
  ];

  const urlsColumns = [
    { key: 'environment', name: COLUMN_ENVIRONMENT },
    { key: 'url', name: COLUMN_URL },
  ];

  const urlsRows = [
    {
      id: 1,
      environment: <PropertyName>{ENV_DEV}</PropertyName>,
      url: (
        <UrlLink
          href={`${BASE_URLS[ENV_DEV]}/${GUEST_SUFFIX}?deeplink=${encodedParams}`}
          rel="noopener noreferrer"
          target="_blank"
        >
          {`${
            BASE_URLS[ENV_DEV]
          }/${GUEST_SUFFIX}?deeplink=${encodedParams.substring(0, 30)}...`}
        </UrlLink>
      ),
    },
    {
      id: 2,
      environment: <PropertyName>{ENV_INT}</PropertyName>,
      url: (
        <UrlLink
          href={`${BASE_URLS[ENV_INT]}/${GUEST_SUFFIX}?deeplink=${encodedParams}`}
          rel="noopener noreferrer"
          target="_blank"
        >
          {`${
            BASE_URLS[ENV_INT]
          }/${GUEST_SUFFIX}?deeplink=${encodedParams.substring(0, 30)}...`}
        </UrlLink>
      ),
    },
    {
      id: 3,
      environment: <PropertyName>{ENV_STAGE}</PropertyName>,
      url: (
        <UrlLink
          href={`${BASE_URLS[ENV_STAGE]}/${GUEST_SUFFIX}?deeplink=${encodedParams}`}
          rel="noopener noreferrer"
          target="_blank"
        >
          {`${
            BASE_URLS[ENV_STAGE]
          }/${GUEST_SUFFIX}?deeplink=${encodedParams.substring(0, 30)}...`}
        </UrlLink>
      ),
    },
    {
      id: 4,
      environment: <PropertyName>{ENV_UAT}</PropertyName>,
      url: (
        <UrlLink
          href={`${BASE_URLS[ENV_UAT]}/${GUEST_SUFFIX}?deeplink=${encodedParams}`}
          rel="noopener noreferrer"
          target="_blank"
        >
          {`${
            BASE_URLS[ENV_UAT]
          }/${GUEST_SUFFIX}?deeplink=${encodedParams.substring(0, 30)}...`}
        </UrlLink>
      ),
    },
    {
      id: 5,
      environment: <PropertyName>{ENV_PROD}</PropertyName>,
      url: (
        <UrlLink
          href={`${BASE_URLS[ENV_PROD]}/${GUEST_SUFFIX}?deeplink=${encodedParams}`}
          rel="noopener noreferrer"
          target="_blank"
        >
          {`${
            BASE_URLS[ENV_PROD]
          }/${GUEST_SUFFIX}?deeplink=${encodedParams.substring(0, 30)}...`}
        </UrlLink>
      ),
    },
  ];

  return (
    <>
      <DeeplinksBanner
        data-testid="deeplinks-banner"
        status="info"
        title={TITLE_DEEPLINKS}
      >
        {BANNER_DEEPLINKS_MESSAGE}
      </DeeplinksBanner>
      <TableWrapper>
        <V1Table
          columns={propertiesColumns}
          highlightRowOnHover
          rows={propertiesRows}
          title={TITLE_DEEPLINK_PROPERTIES}
        />

        <Heading
          color="$core.color.brand.100"
          level={2}
          size="lg"
          style={{ marginTop: '24px', marginBottom: '16px' }}
        >
          {TITLE_EXAMPLE_URLS}
        </Heading>
        <V1Table
          columns={urlsColumns}
          highlightRowOnHover
          rows={urlsRows}
          title={TITLE_ENVIRONMENT_URLS}
        />
      </TableWrapper>
    </>
  );
};
